import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';

export interface OverflowMenuItem {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Extra classes (e.g. `sm:hidden` for an action that is visible on larger screens). */
  className?: string;
}

interface OverflowMenuProps {
  items: OverflowMenuItem[];
  /** Accessible name of the trigger (i18n). */
  label: string;
  /** Render the trigger as a bordered secondary button (default) or a quiet icon. */
  variant?: 'secondary' | 'ghost';
  className?: string;
}

/**
 * "⋯" menu for secondary and destructive actions (DESIGN_SYSTEM.md §10):
 * keeps them out of competition with the primary action. Keyboard: Enter /
 * Space / ArrowDown open, arrows move, Escape closes and returns focus.
 */
export default function OverflowMenu({ items, label, variant = 'secondary', className = '' }: OverflowMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    const first = itemRefs.current.find((el) => el && !el.disabled && el.offsetParent !== null);
    first?.focus();
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const focusAt = (i: number) => {
    const enabled = itemRefs.current.filter((el): el is HTMLButtonElement => !!el && !el.disabled && el.offsetParent !== null);
    if (enabled.length === 0) return;
    enabled[(i + enabled.length) % enabled.length].focus();
  };

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const enabled = itemRefs.current.filter((el): el is HTMLButtonElement => !!el && !el.disabled && el.offsetParent !== null);
    const idx = enabled.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); focusAt(idx + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focusAt(idx - 1); }
    else if (e.key === 'Home') { e.preventDefault(); focusAt(0); }
    else if (e.key === 'End') { e.preventDefault(); focusAt(enabled.length - 1); }
    else if (e.key === 'Escape' || e.key === 'Tab') {
      if (e.key === 'Escape') e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); } }}
        className={variant === 'secondary'
          ? 'btn btn-secondary px-2.5'
          : 'p-1.5 rounded-lg text-surface-500 hover:text-surface-900 hover:bg-surface-100 transition-colors duration-150'}
      >
        <MoreHorizontal size={18} aria-hidden="true" />
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          tabIndex={-1}
          aria-label={label}
          onKeyDown={onMenuKey}
          className="dropdown-menu absolute end-0 top-full mt-1 z-50 focus:outline-none"
        >
          {items.map((it, i) => (
            <button
              key={it.key}
              ref={(el) => { itemRefs.current[i] = el; }}
              type="button"
              role="menuitem"
              disabled={it.disabled}
              onClick={() => { setOpen(false); it.onSelect(); }}
              className={`${it.danger ? 'dropdown-item-danger' : 'dropdown-item'} w-[calc(100%-0.5rem)] text-start disabled:opacity-50 disabled:pointer-events-none ${it.className ?? ''}`}
            >
              {it.icon && <span className="flex-shrink-0" aria-hidden="true">{it.icon}</span>}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
